import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { SidebarItem } from './SidebarItem.js'
import { IconChevronDown, IconChevronRight, IconFolder16 } from '../icons/index.js'
import { apiClient } from '../../lib/api-client.js'
import type { Brand } from '../../lib/api-types.js'

/**
 * The sidebar's Favorites group — DESIGN-HANDOVER.md §8.1 item 5, Figma `133:15971`.
 * A collapsible disclosure header over the caller's favourite brand-folder rows.
 *
 * This group runs on REAL DATA, not a fixture: `GET /brands/favorites` shipped in Epic 48 Phase
 * 02A and its own doc comment says it "backs the sidebar Favorites group's data". Selecting a row
 * scopes the campaign list by writing `?brandId=` — the URL is the single source of truth for
 * brand scope, shared with the page-header brand switcher, so the two can never disagree.
 *
 * TWO STATES THE FIGMA NEVER DRAWS, and what they do here:
 *   - **Empty (no favourites).** Every frame shows exactly four brands; there is no empty-state
 *     illustration or copy anywhere. Rendering an invented empty state would be improvising a
 *     surface, so the group renders its header and no rows. Honest, and not a guess.
 *   - **Overflow (more than four).** No scrollbar, "show more" or pagination is drawn. All rows
 *     render and the sidebar's own column scrolls. Recorded as an assumption, not a decision.
 *
 * The label "Favorites" is the Figma's English placeholder, kept deliberately. §8.1 marks this
 * copy TBD (Turkish) with no settled string, and CLAUDE.md forbids inventing Turkish copy — the
 * same reason `CampaignTable`'s four column headers are still English. Replace it when an owner
 * settles the word, not before.
 */
export function FavoritesGroup() {
  const [open, setOpen] = useState(true) // shown expanded in every evidenced frame
  const [brands, setBrands] = useState<Brand[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [params, setParams] = useSearchParams()
  const activeBrandId = params.get('brandId')

  useEffect(() => {
    let alive = true
    apiClient
      .getFavoriteBrands({ pageSize: 50 })
      .then((res) => {
        if (alive) setBrands(res.data)
      })
      .catch(() => {
        // A failed favourites read must never take the whole shell down with it. The group
        // renders nothing and the rest of the sidebar keeps working.
        if (alive) {
          setBrands([])
          setFailed(true)
        }
      })
    return () => {
      alive = false
    }
  }, [])

  const selectBrand = (id: string) => {
    const next = new URLSearchParams(params)
    // Clicking the already-selected brand clears the scope rather than doing nothing — the only
    // way back to "all campaigns" once a brand is picked, since no "All" row is drawn.
    if (activeBrandId === id) next.delete('brandId')
    else next.set('brandId', id)
    setParams(next)
  }

  return (
    <div className="flex flex-col gap-xsmall">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-small rounded-150 px-medium py-small-medium text-left text-caption-strong text-text-secondary transition-colors hover:bg-surface-secondary focus-visible"
      >
        <span>Favorites</span>
        <span aria-hidden className="flex items-center text-icon-secondary">
          {open ? <IconChevronDown /> : <IconChevronRight />}
        </span>
      </button>

      {open && (
        // Overflow: the group scrolls INSIDE itself rather than pushing the Figma Plugin footer
        // off the bottom of the sidebar. No scrollbar or "show more" is drawn — every frame shows
        // exactly four brands — so this is an assumption, recorded as one, not evidence.
        <div className="max-h-64 space-y-xsmall overflow-y-auto">
          {brands?.map((brand) => (
            <SidebarItem
              key={brand.id}
              icon={<IconFolder16 />}
              label={brand.name}
              selected={activeBrandId === brand.id}
              onClick={() => selectBrand(brand.id)}
            />
          ))}
          {/*
            THREE DISTINCT STATES, and the distinction is the point. `brands === null` is still
            loading and says nothing; `[]` genuinely has no favourites; `failed` could not find
            out. Rendering nothing for all three — which is what this did until 2026-08-28 — makes
            "you have no favourites" indistinguishable from "we are still asking" and from "the
            request died", so a user with favourites and a broken connection sees the same
            emptiness as a user with none.

            The empty line is plain text, not an invented empty-state surface: no illustration, no
            call to action, no affordance. The Figma draws none of those, and adding one would be
            improvising a surface.
          */}
          {brands !== null && brands.length === 0 && !failed && (
            <p className="px-small-medium text-body-small-regular text-text-secondary">
              Henüz favori marka yok.
            </p>
          )}
          {failed && (
            <p className="px-small-medium text-body-small-regular text-text-secondary">
              Favoriler yüklenemedi.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
